import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faEllipsis, faChartLine, faTimes, faLayerGroup, faChartBar, faCandleStickChart, faVolumeHigh, faTags, faBell, faBookmark, faEllipsisV, faTrash, faShare, faPaperPlane, faArrowTrendUp, faArrowTrendDown, faMinus, faPlus, faArrowDownZA, faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { FaApple, FaDollarSign, FaBitcoin, FaEuroSign } from "react-icons/fa";
import { SiTesla, SiNetflix, SiGoogle } from "react-icons/si";
import { MdShowChart, MdOilBarrel } from "react-icons/md";
import upDown from "../../images/updown.png";
import { LuArrowDownUp } from "react-icons/lu";
import { getUserDefaultWatchlistItems, searchStock, createOrder } from "../../api/user";
import { pairSymbol } from "../../utils/instrumentFormat";
import { formatOrderPrice, formatTotal, formatCash, quantityLabel } from "../../utils/orderFormat";
import useFreeCash from "../../hooks/useFreeCash";
import useClosablePosition from "../../hooks/useClosablePosition";
import ForexLotKeypad from "./ForexLotKeypad";
import PositionCloseActions from "./PositionCloseActions";
import { useNewTraderGuide } from "../../context/NewTraderGuideContext";
import { toast } from "react-toastify";
import WheelPicker3D from "./WheelPicker3D";
import { Arrow } from "react-konva";
import ProcessingButton from "../Skeleton/ProcessingButton";
import { useLeagueTrade } from "../../context/LeagueTradeContext";
import {
  trackOrderStarted,
  trackOrderSubmitted,
  isConfirmedFill,
  trackTradeConfirmationViewed,
} from "../../utils/orderMeasurement";
import { isEligibleInLeague, leagueTradeBlockReason } from "../../utils/leagueAssets";

// Maps the order endpoint's existing refusal messages (lang/en/message.php
// STOCK.*, ORDER.ORDER_STATUS, LeagueAssets) to the client's launch copy.
const orderErrorText = (message) => {
  const m = String(message || "");
  if (m.startsWith("You have insufficient coins")) return "Not enough free cash for this order.";
  if (m.startsWith("You have insufficient holdings quantity")) return "Not enough available quantity for this order.";
  if (m.startsWith("You have insufficient short sell quantity")) return "Not enough available quantity for this order.";
  if (m === "The order has already been executed or closed.") return "This order is no longer open.";
  if (m === "This league has not started yet") return "Trading has not started in this league.";
  if (m === "This league has ended") return "This league has ended.";
  if (m === "Not eligible in this league") return "This asset is not eligible for this league.";
  if (m === "The order price field must be greater than 0.") return "Enter a price above 0.";
  return "Could not place the order. Try again.";
};

const MAX_INDICATORS = 2;

const stockIcons = {
  "AAPL": FaApple,
  "SPY": MdShowChart,
  "CSCO": MdShowChart,
  "X:BTCUSD": FaBitcoin,
  "C:EURUSD": FaEuroSign,
  "TSLA": SiTesla,
  "NFLX": SiNetflix,
  "GOOGL": SiGoogle,
};

// Stock icon colors
const stockColors = {
  "AAPL": "#A2AAAD",
  "SPY": "#00D084",
  "CSCO": "#00BCEB",
  "X:BTCUSD": "#F7931A",
  "C:EURUSD": "#0052FF",
  "TSLA": "#CC0000",
  "NFLX": "#E50914",
  "GOOGL": "#4285F4",
};

const slideUpStyles = `
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

/**
 * The Overlay / Indicator picker, drawn by us instead of by the phone.
 *
 * Client: "there is no reason to have 'Select Overlay or Indicator' in the list
 * on dekstop and mobile a user can click outside the options and has clear all.
 * No reason for the checkmarked 'Select Indicator' to appear in the list."
 *
 * Desktop got that by marking the prompt `hidden`, and that was enough there.
 * On a phone a <select> hands its list to the OPERATING SYSTEM: iOS builds its
 * own picker, ignores `hidden` on an option, and draws its own checkmark against
 * the current row. Confirmed on an iPhone in BOTH the QUSHi app and plain mobile
 * Safari — so it is the platform control, not the WebView, and no markup can
 * reach it. The only way to control that list is to stop using it.
 *
 * NOTHING ABOUT THE CLOSED FIELD MAY CHANGE, so the button is handed the exact
 * className and style of the <select> it replaces: same border, radius, padding,
 * font size, and the same chevron background image. `text-left` and `truncate`
 * are not new styling either — they restore what a <select> does by default and
 * a <button> does not (align its text left, and keep it to one clipped line).
 *
 * The option list renders IN FLOW beneath the row rather than absolutely
 * positioned. Its container is `max-h-[350px] overflow-y-auto overflow-x-hidden`
 * and an absolute panel inside that would simply be clipped — the same trap that
 * hid a correctly-measured chart behind an ancestor's overflow in §2n.
 */
const MobileSelectField = ({ value, options, placeholder, className, style, onToggle }) => {
  // The empty value is the prompt, and the prompt is never a selection.
  const selected = options.find((option) => option.value !== "" && option.value === value);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`${className} text-left truncate`}
      style={style}
    >
      {selected ? selected.label : placeholder}
    </button>
  );
};

/**
 * The options themselves — the real choices and nothing else. No prompt row, and
 * no checkmark on the current one, because both are what the client asked to be
 * rid of.
 */
const MobileSelectList = ({ options, itemClassName, onSelect }) => (
  <div
    className="mt-1 border border-gray-300 rounded-lg bg-white max-h-56 overflow-y-auto"
    onClick={(e) => e.stopPropagation()}
  >
    {options
      .filter((option) => option.value !== "")
      .map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(option.value);
          }}
          className={`block w-full text-left px-3 py-2.5 bg-white text-primaryColor ${itemClassName}`}
        >
          {option.label}
        </button>
      ))}
  </div>
);

const MobileChartToolbar = ({
  onStockSelect,
  onPeriodSelect,
  onPencilClick,
  onIndicatorsClick,
  onMenuClick,
  onAlertClick,
  onOverlayUpdate,
  onIndicatorUpdate,
  onWatchlistClick,
  onSaveChartClick,
  onDirectShareChart,
  savedCharts,
  onSavedChartClick,
  onDeleteChart,
  onShareChartAsPost,
  selectedStock,
  selectedPeriod,
  setting,
  setSetting,
  indicatorRows,
  setIndicatorRows,
  editOrder,
  onEditOrderDone,
}) => {
  const isEditMode = !!editOrder?.id;
  // The ticker order_started was last logged for (Measurement Core §6.5);
  // cleared when the sheet closes (including after an order is placed), so
  // the next order starts again.
  const startedFor = useRef(null);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showChartTypeModal, setShowChartTypeModal] = useState(false);
  const [showOverlaysModal, setShowOverlaysModal] = useState(false);
  const [showIndicatorsModal, setShowIndicatorsModal] = useState(false);
  const [showSavedChartsModal, setShowSavedChartsModal] = useState(false);
  const [showBuySellModal, setShowBuySellModal] = useState(false);
  const [buySellLoading, setBuySellLoading] = useState(false);
  const league = useLeagueTrade();
  const { freeCash } = useFreeCash({
    gameId: league?.gameId || null,
    enabled: showBuySellModal,
  });
  const [tradeType, setTradeType] = useState(1);
  const [activeOrderTab, setActiveOrderTab] = useState("limit");
  const [quantity, setQuantity] = useState(0);
  const [forexLotSize, setForexLotSize] = useState(null);
  const [forexLotCount, setForexLotCount] = useState(1);
  const [limitPrice, setLimitPrice] = useState(0);
  const [lossPrice, setLossPrice] = useState(0);
  const [stockData, setStockData] = useState(null);
  const [marketPrice, setMarketPrice] = useState(0);
  const [showQuantityPopup, setShowQuantityPopup] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({});
  const [overlayRows, setOverlayRows] = useState([{ selectValue: "", inputValue: "" }]);

  // The position this ticket could close, in whichever book it is trading —
  // main account, league or Cash Game (client rule 2). Same hook the desktop
  // ticket uses, so the two screens can never offer different numbers for the
  // same position. Only read while the ticket is actually open.
  const { position, refreshPosition } = useClosablePosition({
    symbol: stockData?.ticker,
    gameId: league?.gameId || null,
    enabled: showBuySellModal && !!stockData?.ticker,
  });

  // Client: "Selecting one fills the quantity, selects the correct trade
  // direction, and closes the quantity menu."
  const applyPositionAction = (action) => {
    setQuantity(action.quantity);
    setTradeType(action.tradeType);
    setShowQuantityPopup(false);
  };

  const [showStockWheelPicker, setShowStockWheelPicker] = useState(false);
  const [showPeriodWheelPicker, setShowPeriodWheelPicker] = useState(false);

  const [wheelScrollState, setWheelScrollState] = useState(null);
  const [periodWheelScrollState, setPeriodWheelScrollState] = useState(null);

  const [stockInitialTouchY, setStockInitialTouchY] = useState(null);
  const [periodInitialTouchY, setPeriodInitialTouchY] = useState(null);

  const desiredStockSymbolRef = useRef(null);
  const desiredPeriodIdRef = useRef(null);
  const desiredStockTimerRef = useRef(null);
  const desiredPeriodTimerRef = useRef(null);

  const [modalSwipes, setModalSwipes] = useState({});

  const getSwipeOffset = (modalId) => {
    const s = modalSwipes[modalId];
    if (!s) return 0;
    return Math.max(0, s.currentY - s.startY);
  };

  const getModalSwipeStyle = (modalId, extraStyle = {}) => {
    const s = modalSwipes[modalId];
    const offset = getSwipeOffset(modalId);
    const isDragging = s?.isDragging || false;
    return {
      animation: isDragging ? 'none' : 'slideUp 0.3s ease-out',
      transform: `translateY(${offset}px)`,
      transition: isDragging ? 'none' : 'transform 0.3s ease-out',
      ...extraStyle,
    };
  };

  const createModalSwipeHandlers = (setShowModal, modalId) => ({
    onTouchStart: (e) => {
      e.stopPropagation();
      setModalSwipes((prev) => ({
        ...prev,
        [modalId]: {
          startY: e.touches[0].clientY,
          currentY: e.touches[0].clientY,
          isDragging: true,
        },
      }));
    },
    onTouchMove: (e) => {
      e.stopPropagation();
      setModalSwipes((prev) => {
        const s = prev[modalId];
        if (!s?.isDragging) return prev;
        return {
          ...prev,
          [modalId]: { ...s, currentY: e.touches[0].clientY },
        };
      });
    },
    onTouchEnd: (e) => {
      e.stopPropagation();
      const s = modalSwipes[modalId];
      if (s) {
        const swipeDistance = s.currentY - s.startY;
        if (swipeDistance > 30) {
          setShowModal(false);
        }
      }
      setModalSwipes((prev) => {
        const next = { ...prev };
        delete next[modalId];
        return next;
      });
    },
  });

  // Which row's picker is open, by index, or null. One per sheet — the two
  // sheets are never on screen together, but keeping them separate means
  // neither can leave the other's list hanging open.
  const [openOverlayPicker, setOpenOverlayPicker] = useState(null);
  const [openIndicatorPicker, setOpenIndicatorPicker] = useState(null);

  const overlayOptions = [
    // Client, 3: "Select overlay" replaces "----None----". Mobile carries its
    // own copy of this picker, so both are changed together.
    { value: "", label: "Select overlay", defaultParams: "" },
    { value: "bb", label: "Bollinger Bands", defaultParams: "20,2" },
    { value: "sma", label: "Simple Moving Avg", defaultParams: "50" },
    { value: "ema", label: "Exp Moving Avg", defaultParams: "50" },
    { value: "psar", label: "Parabolic SAR", defaultParams: "0.09,0.2" },
  ];

  const indicatorOptions = [
    { value: "", label: "Select indicator", defaultParams: "", position: "above" },
    { value: "adl", label: "Accumulation/Distribution Line (ADL)", defaultParams: "0", position: "below" },
    { value: "adx", label: "ADX Line (w/+DI and -DI)", defaultParams: "14", position: "below" },
    { value: "cci", label: "Commodity Channel Index (CCI)", defaultParams: "20", position: "below" },
    { value: "cmf", label: "Chaikin Money Flow (CMF)", defaultParams: "20", position: "below" },
    { value: "macd", label: "MACD", defaultParams: "12,26,9", position: "below" },
    { value: "macdh", label: "MACD Histogram", defaultParams: "12,26,9", position: "below" },
    { value: "mfi", label: "Money Flow Index (MFI)", defaultParams: "14", position: "below" },
    { value: "obv", label: "On Balance Volume (OBV)", defaultParams: "0", position: "below" },
    { value: "ppo", label: "Pct Price Oscillator (PPO)", defaultParams: "12,26,9", position: "below" },
    { value: "pvo", label: "Pct Volume Oscillator (PVO)", defaultParams: "12,26,9", position: "below" },
    { value: "roc", label: "Rate of Change (ROC)", defaultParams: "12", position: "below" },
    { value: "efi", label: "Force Index (EFI)", defaultParams: "13", position: "below" },
    { value: "stoch", label: "Full Stochastics (STOCH)", defaultParams: "14,3,3", position: "below" },
    { value: "rsi", label: "Relative Strength Index (RSI)", defaultParams: "14", position: "below" },
  ];

  // Generate random color for overlay
  const generateRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const handleOpenOverlaysModal = () => {
    if (setting?.overlayParams && setting.overlayParams.length > 0) {
      const currentOverlays = setting.overlayParams.map(row => ({
        selectValue: row.selectValue || "",
        inputValue: row.inputValue || "",
        color: row.color || generateRandomColor()
      }));
      if (currentOverlays.length < 10) {
        currentOverlays.push({ selectValue: "", inputValue: "" });
      }
      setOverlayRows(currentOverlays);
    } else {
      setOverlayRows([{ selectValue: "", inputValue: "" }]);
    }
    setShowOverlaysModal(true);
  };

  // Handle overlay select change
  const handleOverlaySelectChange = (index, value) => {
    const newRows = [...overlayRows];
    newRows[index].selectValue = value;
    newRows[index].color = generateRandomColor();

    // Set default params based on selection
    const option = overlayOptions.find(o => o.value === value);
    newRows[index].inputValue = option?.defaultParams || "";

    // Add new row if this is the last row and not "None"
    if (value !== "" && index === newRows.length - 1 && newRows.length < 10) {
      newRows.push({ selectValue: "", inputValue: "" });
    }

    setOverlayRows(newRows);
  };

  // Handle overlay input change
  const handleOverlayInputChange = (index, value) => {
    const newRows = [...overlayRows];
    newRows[index].inputValue = value;
    setOverlayRows(newRows);
  };

  // Handle overlay update
  const handleOverlaySubmit = () => {
    // Filter out rows with "None" selected (empty selectValue)
    const validRows = overlayRows.filter(row => row.selectValue !== "");

    if (validRows.length > 0) {
      // Has valid overlays - update them
      if (setSetting) {
        setSetting(prev => ({ ...prev, overlayParams: validRows, clearOverlay: false }));
      }
      if (onOverlayUpdate) {
        onOverlayUpdate();
      }
    } else {
      // All overlays are "None" - clear them
      if (setSetting) {
        setSetting(prev => ({ ...prev, overlayParams: [], clearOverlay: true }));
      }
      if (onOverlayUpdate) {
        onOverlayUpdate();
      }
    }
    setShowOverlaysModal(false);
  };

  // Handle clear all overlays
  const handleOverlayClearAll = () => {
    setOverlayRows([{ selectValue: "", inputValue: "" }]);
    if (setSetting) {
      setSetting(prev => ({ ...prev, overlayParams: [], clearOverlay: true }));
    }
    if (onOverlayUpdate) {
      onOverlayUpdate();
    }
    setShowOverlaysModal(false);
  };

  const indicatorRowsForRender =
    indicatorRows || [{ selectValue: "", inputValue: "", selectPosValue: "above" }];
  const chosenIndicatorRows = indicatorRowsForRender.filter(
    (r) => r.selectValue !== ""
  );
  const visibleIndicatorRows =
    chosenIndicatorRows.length >= MAX_INDICATORS
      ? chosenIndicatorRows
      : [
          ...chosenIndicatorRows,
          indicatorRowsForRender.find((r) => r.selectValue === "") || {
            selectValue: "",
            inputValue: "",
            selectPosValue: "above",
          },
        ];

  // Handle indicator select change
  const handleIndicatorSelectChange = (index, value) => {
    if (!setIndicatorRows || !indicatorRows) return;

    const wasEmpty = indicatorRows[index]?.selectValue === "";
    const currentActive = indicatorRows.filter((r) => r.selectValue !== "").length;
    if (wasEmpty && value !== "" && currentActive >= MAX_INDICATORS) {
      return;
    }

    const newRows = [...indicatorRows];
    newRows[index].selectValue = value;

    const option = indicatorOptions.find(o => o.value === value);
    newRows[index].inputValue = option?.defaultParams || "";
    newRows[index].selectPosValue = option?.position || "above";

    if (value !== "" && index === newRows.length - 1 && newRows.length < 10) {
      newRows.push({ selectValue: "", inputValue: "", selectPosValue: "above" });
    }

    setIndicatorRows(newRows);

    const nonEmptyRows = newRows.filter(row => row.selectValue !== "");
    if (setSetting) {
      setSetting(prev => ({
        ...prev,
        indicatorParams: nonEmptyRows,
        clearIndicator: false,
      }));
    }
  };

  const handleIndicatorInputChange = (index, value) => {
    if (!setIndicatorRows || !indicatorRows) return;

    const newRows = [...indicatorRows];
    newRows[index].inputValue = value || "";
    setIndicatorRows(newRows);
  };

  // Handle indicator position change
  const handleIndicatorPositionChange = (index, value) => {
    if (!setIndicatorRows || !indicatorRows) return;

    const newRows = [...indicatorRows];
    newRows[index].selectPosValue = value;
    setIndicatorRows(newRows);
  };

  // Handle indicator submit
  const handleIndicatorSubmit = () => {
    if (!indicatorRows) return;

    const cleanedRows = indicatorRows.filter(row => row.selectValue !== "");

    if (cleanedRows.length === 0) {
      // Reset properly
      if (setIndicatorRows) {
        setIndicatorRows([{ selectValue: "", inputValue: "", selectPosValue: "above" }]);
      }
      if (setSetting) {
        setSetting(prev => ({
          ...prev,
          indicatorParams: [],
          clearIndicator: true,
        }));
      }
      setShowIndicatorsModal(false);
      return;
    }

    // Always append an empty row at the end
    const finalRows = cleanedRows.length < 10
      ? [...cleanedRows, { selectValue: "", inputValue: "", selectPosValue: "above" }]
      : cleanedRows;

    if (setIndicatorRows) {
      setIndicatorRows(finalRows);
    }
    if (setSetting) {
      setSetting(prev => ({
        ...prev,
        indicatorParams: cleanedRows,
        clearIndicator: false,
      }));
    }
    if (onIndicatorUpdate) {
      onIndicatorUpdate();
    }
    setShowIndicatorsModal(false);
  };

  // Handle clear all indicators
  const handleIndicatorClearAll = () => {
    if (setIndicatorRows) {
      setIndicatorRows([{ selectValue: "", inputValue: "", selectPosValue: "above" }]);
    }
    if (setSetting) {
      setSetting(prev => ({
        ...prev,
        indicatorParams: [],
        clearIndicator: true,
      }));
    }
    setShowIndicatorsModal(false);
  };

  // Toggle dropdown for saved chart menu
  const toggleDropdown = (index) => {
    setDropdownStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const chartTypes = [
    { value: "candlestick", label: "Candlestick", icon: "candlestick" },
    { value: "ohlc", label: "OHLC Bars", icon: "ohlc" },
    { value: "solid", label: "Solid Line", icon: "solid" },
  ];

  const getCategoryFromSymbol = (symbol) => {
    if (symbol.startsWith("C:")) return "Forex";
    if (symbol.startsWith("X:")) return "Crypto";
    return "Stocks";
  };

  // Default stocks to display (includes Stocks, Crypto, and Forex)
  const SITE_DEFAULT_STOCKS = [
    { symbol: "AAPL", name: "Apple", category: "Stocks" },
    { symbol: "SPY", name: "S&P 500 ETF", category: "Stocks" },
    { symbol: "CSCO", name: "Cisco", category: "Stocks" },
    { symbol: "X:BTCUSD", name: "Bitcoin", category: "Crypto" },
    { symbol: "C:EURUSD", name: "EUR/USD", category: "Forex" },
    { symbol: "TSLA", name: "Tesla", category: "Stocks" },
    { symbol: "NFLX", name: "Netflix", category: "Stocks" },
    { symbol: "GOOGL", name: "Google", category: "Stocks" },
  ];

  const defaultStocks = useMemo(() => {
    if (!league?.game) return SITE_DEFAULT_STOCKS;
    if (league.symbols.length) {
      return league.symbols.map((symbol) => ({
        symbol,
        name: symbol,
        category: getCategoryFromSymbol(symbol),
      }));
    }
    return SITE_DEFAULT_STOCKS.filter((s) => isEligibleInLeague(league.game, s.symbol));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  const [stocks, setStocks] = useState(defaultStocks);
  const navigate = useNavigate();
  const { guideActive, guideStep, advanceStep, completeGuide } = useNewTraderGuide();

  const guideAutofillDone = () => localStorage.getItem("qushi_guide_step3_consumed") === "true";
  const markGuideAutofillDone = () => localStorage.setItem("qushi_guide_step3_consumed", "true");
  const [showGuideTooltip, setShowGuideTooltip] = useState(false);

  const closeBuySellModal = () => {
    setShowBuySellModal(false);
    setShowGuideTooltip(false);
    if (guideActive && guideStep === 3) {
      completeGuide();
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  useEffect(() => {
    const sidebar = document.getElementById("sidebar");
    if (!sidebar) return;
    const compute = () => {
      setIsSidebarOpen(!sidebar.classList.contains("-translate-x-full"));
    };
    compute();
    const observer = new MutationObserver(compute);
    observer.observe(sidebar, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const handleStockPickerClick = () => {
    if (!stockAllowClick.current) {
      stockAllowClick.current = true;
      return;
    }
    setShowStockWheelPicker(true);
  };

  const handlePeriodPickerClick = () => {
    setShowPeriodWheelPicker(true);
  };


  const quickPeriods = [
    { id: 1, display: "1 minute", shortDisplay: "1m", internal: "minute", multiplier: 1 },
    { id: 2, display: "5 minutes", shortDisplay: "5m", internal: "minute", multiplier: 5 },
    { id: 3, display: "15 minutes", shortDisplay: "15m", internal: "minute", multiplier: 15 },
    { id: 4, display: "30 minutes", shortDisplay: "30m", internal: "minute", multiplier: 30 },
    { id: 5, display: "1 hour", shortDisplay: "1h", internal: "hour", multiplier: 1 },
    { id: 6, display: "4 hours", shortDisplay: "4h", internal: "hour", multiplier: 4 },
    { id: 7, display: "Daily", shortDisplay: "D", internal: "day", multiplier: 1 },
    { id: 8, display: "Weekly", shortDisplay: "W", internal: "week", multiplier: 1 },
    { id: 9, display: "Monthly", shortDisplay: "M", internal: "month", multiplier: 1 },
  ];

  // Get the current selected stock from localStorage or props
  const currentStock = selectedStock || localStorage.getItem("ticker") || "AAPL";

  // Get the current selected period from setting - default to 1h (id: 5)
  const getCurrentPeriodId = () => {
    if (!setting) return 5; // Default to 1h
    const period = quickPeriods.find(
      p => p.internal === setting.period && p.multiplier === setting.multiplier
    );
    return period?.id || 5; // Default to 1h
  };

  const [stockIndex, setStockIndex] = useState(() => {
    const idx = stocks.findIndex(s => s.symbol === currentStock);
    return idx >= 0 ? idx : 0;
  });
  const [periodIndex, setPeriodIndex] = useState(() => {
    const periodId = getCurrentPeriodId();
    const idx = quickPeriods.findIndex(p => p.id === periodId);
    return idx >= 0 ? idx : 4;
  });

  // Track if user is currently scrolling
  const [isScrollingStock, setIsScrollingStock] = useState(false);
  const [isScrollingPeriod, setIsScrollingPeriod] = useState(false);

  // Refs for scroll state
  const stockScrollTimer = useRef(null);
  const periodScrollTimer = useRef(null);
  const confirmedStockIndex = useRef(stockIndex);
  const confirmedPeriodIndex = useRef(periodIndex);

  // Handle stock selection from wheel picker
  const handleWheelStockSelect = useCallback((index, stock) => {
    if (!stock) return;

    desiredStockSymbolRef.current = stock.symbol;
    clearTimeout(desiredStockTimerRef.current);
    desiredStockTimerRef.current = setTimeout(() => { desiredStockSymbolRef.current = null; }, 10000);

    setStockIndex(index);
    confirmedStockIndex.current = index;
    if (onStockSelect) {
      onStockSelect(stock);
    }
  }, [onStockSelect]);

  // Handle period selection from wheel picker
  const handleWheelPeriodSelect = useCallback((index, period) => {
    if (!period) return;

    // Always lock the desired period so the mini-scroller shows this period
    desiredPeriodIdRef.current = period.id;
    clearTimeout(desiredPeriodTimerRef.current);
    desiredPeriodTimerRef.current = setTimeout(() => { desiredPeriodIdRef.current = null; }, 2000);

    setPeriodIndex(index);
    confirmedPeriodIndex.current = index;
    if (onPeriodSelect) {
      onPeriodSelect(period);
    }
  }, [onPeriodSelect]);

  // Sync with external changes (when currentStock or stocks list changes)
  useEffect(() => {
    if (isScrollingStock) return; // Don't sync while user is scrolling
    if (desiredStockSymbolRef.current) return; // Don't sync while settling from modal selection

    const idx = stocks.findIndex(s => s.symbol === currentStock);
    if (idx >= 0) {
      setStockIndex(idx);
      confirmedStockIndex.current = idx;
    } else if (currentStock && stocks.length > 0) {
      // Current stock not in list, add it at the beginning
      const currentStockItem = {
        symbol: currentStock,
        name: currentStock,
        category: getCategoryFromSymbol(currentStock),
      };
      setStocks(prev => {
        // Check if already added to prevent infinite loop
        if (prev[0]?.symbol === currentStock) return prev;
        return [currentStockItem, ...prev];
      });
      setStockIndex(0);
      confirmedStockIndex.current = 0;
    }
  }, [currentStock, stocks, isScrollingStock]);

  useEffect(() => {
    const periodId = getCurrentPeriodId();
    const idx = quickPeriods.findIndex(p => p.id === periodId);
    if (idx >= 0 && !isScrollingPeriod) {
      setPeriodIndex(idx);
      confirmedPeriodIndex.current = idx;
    }
  }, [setting, isScrollingPeriod]);


  useEffect(() => {
    const fetchDefaultItems = async () => {
      if (desiredStockSymbolRef.current) return;

      try {
        const res = await getUserDefaultWatchlistItems();

        if (desiredStockSymbolRef.current) return;

        if (res?.data?.items && res.data.items.length > 0) {
          const fetchedStocks = res.data.items.map((item) => ({
            symbol: item.symbol,
            name: item.watchlist_name || item.symbol,
            category: getCategoryFromSymbol(item.symbol),
          }));

          // Check if the current stock is in the fetched list
          const currentTicker = selectedStock || localStorage.getItem("ticker") || "AAPL";
          const existingIndex = fetchedStocks.findIndex(s => s.symbol === currentTicker);

          let finalStocks = fetchedStocks;
          let newIndex = existingIndex;

          // If current stock is not in the list, add it at the beginning
          if (existingIndex === -1) {
            const currentStockItem = {
              symbol: currentTicker,
              name: currentTicker, // Will use symbol as name if not known
              category: getCategoryFromSymbol(currentTicker),
            };
            finalStocks = [currentStockItem, ...fetchedStocks];
            newIndex = 0; // Current stock is now at index 0
          }

          setStocks(finalStocks);
          // Update stock index to point to the current stock
          setStockIndex(newIndex >= 0 ? newIndex : 0);
          confirmedStockIndex.current = newIndex >= 0 ? newIndex : 0;
        } else {
          // No items from API, check if current stock is in default list
          const currentTicker = selectedStock || localStorage.getItem("ticker") || "AAPL";
          const existingIndex = defaultStocks.findIndex(s => s.symbol === currentTicker);

          if (existingIndex === -1) {
            // Add current stock to default list
            const currentStockItem = {
              symbol: currentTicker,
              name: currentTicker,
              category: getCategoryFromSymbol(currentTicker),
            };
            const newStocks = [currentStockItem, ...defaultStocks];
            setStocks(newStocks);
            setStockIndex(0);
            confirmedStockIndex.current = 0;
          } else {
            setStockIndex(existingIndex);
            confirmedStockIndex.current = existingIndex;
          }
        }
      } catch (error) {
        // Re-check after await in error path too
        if (desiredStockSymbolRef.current) return;

        console.error("❌ Failed fetching default items:", error);
        // On error, ensure current stock is selected in default list
        const currentTicker = selectedStock || localStorage.getItem("ticker") || "AAPL";
        const existingIndex = defaultStocks.findIndex(s => s.symbol === currentTicker);

        if (existingIndex === -1) {
          const currentStockItem = {
            symbol: currentTicker,
            name: currentTicker,
            category: getCategoryFromSymbol(currentTicker),
          };
          const newStocks = [currentStockItem, ...defaultStocks];
          setStocks(newStocks);
          setStockIndex(0);
          confirmedStockIndex.current = 0;
        } else {
          setStockIndex(existingIndex);
          confirmedStockIndex.current = existingIndex;
        }
      }
    };

    fetchDefaultItems();
  }, [selectedStock]);





  const finalizeStockSelection = useCallback(() => {
    setIsScrollingStock(false);
    const stock = stocks[stockIndex];
    if (stock && stockIndex !== confirmedStockIndex.current) {
      confirmedStockIndex.current = stockIndex;
      desiredStockSymbolRef.current = stock.symbol;
      clearTimeout(desiredStockTimerRef.current);
      desiredStockTimerRef.current = setTimeout(() => {
        desiredStockSymbolRef.current = null;
      }, 10000);
      if (onStockSelect) {
        onStockSelect(stock);
      }
    }
  }, [stockIndex, onStockSelect]);

  useEffect(() => {
    if (!desiredStockSymbolRef.current) return;
    if (
      String(currentStock || "").toUpperCase() ===
      String(desiredStockSymbolRef.current).toUpperCase()
    ) {
      desiredStockSymbolRef.current = null;
      clearTimeout(desiredStockTimerRef.current);
    }
  }, [currentStock]);

  const finalizePeriodSelection = useCallback(() => {
    setIsScrollingPeriod(false);
    const period = quickPeriods[periodIndex];
    if (period && periodIndex !== confirmedPeriodIndex.current) {
      confirmedPeriodIndex.current = periodIndex;
      if (onPeriodSelect) {
        onPeriodSelect(period);
      }
    }
  }, [periodIndex, onPeriodSelect]);

  const stockTouchStart = useRef(null);
  const stockTouchAccum = useRef(0);
  const stockTouchStartTime = useRef(null);
  const stockDidScroll = useRef(false);
  const stockAllowClick = useRef(true);

  const handleStockTouchStart = (e) => {
    stockTouchStart.current = e.touches[0].clientY;
    stockTouchAccum.current = 0;
    stockTouchStartTime.current = Date.now();
    stockDidScroll.current = false;
    stockAllowClick.current = true;
    setIsScrollingStock(true);
    clearTimeout(stockScrollTimer.current);
  };

  const handleStockTouchMove = (e) => {
    if (stockTouchStart.current === null) return;
    e.preventDefault();

    // Any touch move disables click navigation
    stockAllowClick.current = false;

    const currentY = e.touches[0].clientY;
    const diff = stockTouchStart.current - currentY;
    stockTouchAccum.current += diff;
    stockTouchStart.current = currentY;

    const threshold = 25; // Scroll sensitivity

    if (Math.abs(stockTouchAccum.current) >= threshold) {
      const direction = stockTouchAccum.current > 0 ? 1 : -1;
      stockTouchAccum.current = 0;
      stockDidScroll.current = true; // Mark that scrolling occurred

      setStockIndex(prev => {
        // Circular scrolling - wrap around
        let newIndex = prev + direction;
        if (newIndex < 0) newIndex = stocks.length - 1;
        if (newIndex >= stocks.length) newIndex = 0;
        return newIndex;
      });
    }
  };

  const handleStockTouchEnd = () => {
    // Check if it was a long press (> 200ms) - disable click if so
    const touchDuration = Date.now() - (stockTouchStartTime.current || 0);
    if (touchDuration > 200 || stockDidScroll.current) {
      stockAllowClick.current = false;
    }

    stockTouchStart.current = null;
    stockTouchAccum.current = 0;
    stockTouchStartTime.current = null;
    // Delay before finalizing - API call happens after this
    stockScrollTimer.current = setTimeout(finalizeStockSelection, 400);
  };

  // Touch scroll handling for periods
  const periodTouchStart = useRef(null);
  const periodTouchAccum = useRef(0);

  const handlePeriodTouchStart = (e) => {
    periodTouchStart.current = e.touches[0].clientY;
    periodTouchAccum.current = 0;
    setIsScrollingPeriod(true);
    clearTimeout(periodScrollTimer.current);
  };

  const handlePeriodTouchMove = (e) => {
    if (periodTouchStart.current === null) return;
    e.preventDefault();

    const currentY = e.touches[0].clientY;
    const diff = periodTouchStart.current - currentY;
    periodTouchAccum.current += diff;
    periodTouchStart.current = currentY;

    const threshold = 25;

    if (Math.abs(periodTouchAccum.current) >= threshold) {
      const direction = periodTouchAccum.current > 0 ? 1 : -1;
      periodTouchAccum.current = 0;

      setPeriodIndex(prev => {
        // Circular scrolling - wrap around
        let newIndex = prev + direction;
        if (newIndex < 0) newIndex = quickPeriods.length - 1;
        if (newIndex >= quickPeriods.length) newIndex = 0;
        return newIndex;
      });
    }
  };

  const handlePeriodTouchEnd = () => {
    periodTouchStart.current = null;
    periodTouchAccum.current = 0;
    periodScrollTimer.current = setTimeout(finalizePeriodSelection, 400);
  };

  // Mouse wheel handling for stocks
  const handleStockWheel = (e) => {
    e.preventDefault();
    setIsScrollingStock(true);
    clearTimeout(stockScrollTimer.current);

    const direction = e.deltaY > 0 ? 1 : -1;
    setStockIndex(prev => {
      // Circular scrolling - wrap around
      let newIndex = prev + direction;
      if (newIndex < 0) newIndex = stocks.length - 1;
      if (newIndex >= stocks.length) newIndex = 0;
      return newIndex;
    });

    stockScrollTimer.current = setTimeout(finalizeStockSelection, 500);
  };

  // Mouse wheel handling for periods
  const handlePeriodWheel = (e) => {
    e.preventDefault();
    setIsScrollingPeriod(true);
    clearTimeout(periodScrollTimer.current);

    const direction = e.deltaY > 0 ? 1 : -1;
    setPeriodIndex(prev => {
      // Circular scrolling - wrap around
      let newIndex = prev + direction;
      if (newIndex < 0) newIndex = quickPeriods.length - 1;
      if (newIndex >= quickPeriods.length) newIndex = 0;
      return newIndex;
    });

    periodScrollTimer.current = setTimeout(finalizePeriodSelection, 500);
  };

  // Cleanup timers
  useEffect(() => {
    return () => {
      clearTimeout(stockScrollTimer.current);
      clearTimeout(periodScrollTimer.current);
    };
  }, []);

  // Get stock icon component
  const getStockIcon = (symbol) => {
    const IconComponent = stockIcons[symbol];
    return IconComponent || MdShowChart;
  };

  // Render the rotating wheel picker overlay for stocks
  const renderStockOverlay = () => {
    if (!isScrollingStock) return null;

    const visibleItems = 5; // 5 rows: 2 top, 1 selected, 2 bottom
    const halfVisible = Math.floor(visibleItems / 2);
    const itemHeight = 40; // Item height
    const rowSpacing = itemHeight * 0.85; // Tighter spacing between rows

    // Create circular items array
    const circularItems = [];
    for (let i = -halfVisible; i <= halfVisible; i++) {
      const actualIndex = ((stockIndex + i) % stocks.length + stocks.length) % stocks.length;
      circularItems.push({
        stock: stocks[actualIndex],
        distance: i,
        actualIndex
      });
    }

    const totalHeight = (rowSpacing * (visibleItems - 1)) + itemHeight + 8; // Exact height for content

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none md:hidden">
        <div
          className="bg-white/95 backdrop-blur-sm rounded-2xl pointer-events-auto overflow-hidden border border-gray-200 shadow-2xl"
          onTouchStart={handleStockTouchStart}
          onTouchMove={handleStockTouchMove}
          onTouchEnd={handleStockTouchEnd}
          onWheel={handleStockWheel}
          style={{
            touchAction: 'none',
            height: `${totalHeight}px`,
            minWidth: '180px',
            perspective: '1000px'
          }}
        >
          {/* 3D Wheel container */}
          <div
            className="flex flex-col items-center justify-center h-full relative"
            style={{
              transformStyle: 'preserve-3d'
            }}
          >
            {circularItems.map(({ stock, distance, actualIndex }) => {
              const isActive = distance === 0;
              const absDistance = Math.abs(distance);

              // iOS-style 3D rotation calculation
              const rotationAngle = distance * 12; // Less rotation for subtler 3D effect
              const translateZ = -absDistance * 5; // Less depth
              const translateY = distance * rowSpacing; // Tighter row spacing

              // Smooth scaling: 1 at center, decreasing towards edges (higher minimum)
              const scale = Math.max(0.9, 1 - absDistance * 0.05);

              // Smooth opacity: 1 at center, fading towards edges
              const opacity = Math.max(0.6, 1 - absDistance * 0.15);

              // Font size interpolation: larger at center, smaller at edges (bigger minimum)
              const fontSize = Math.max(14, 17 - absDistance * 1);

              // Font weight: Only selected item gets bold (700), all others are normal (400)
              const fontWeight = isActive ? 700 : 400;

              return (
                <div
                  key={`${stock.symbol}-${distance}`}
                  className="absolute flex items-center justify-center w-full"
                  style={{
                    height: `${itemHeight}px`,
                    transform: `
                      translateY(${translateY}px)
                      translateZ(${translateZ}px)
                      rotateX(${-rotationAngle}deg)
                      scale(${scale})
                    `,
                    opacity,
                    zIndex: 10 - absDistance,
                    transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
                    backgroundColor: isActive ? 'rgb(0, 76, 80)' : 'transparent'
                  }}
                >
                  <span
                    className="text-center px-4 py-1"
                    style={{
                      color: isActive ? '#ffffff' : '#6b7280',
                      fontSize: `${fontSize}px`,
                      fontWeight,
                      transition: 'font-weight 0.4s ease-in-out, font-size 0.25s ease-out, color 0.3s ease-out'
                    }}
                  >
                    {pairSymbol(stock.symbol)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render the rotating wheel picker overlay for periods
  const renderPeriodOverlay = () => {
    if (!isScrollingPeriod) return null;

    const visibleItems = 5; // 5 rows: 2 top, 1 selected, 2 bottom
    const halfVisible = Math.floor(visibleItems / 2);
    const itemHeight = 40; // Item height
    const rowSpacing = itemHeight * 0.85; // Tighter spacing between rows

    // Create circular items array
    const circularItems = [];
    for (let i = -halfVisible; i <= halfVisible; i++) {
      const actualIndex = ((periodIndex + i) % quickPeriods.length + quickPeriods.length) % quickPeriods.length;
      circularItems.push({
        period: quickPeriods[actualIndex],
        distance: i,
        actualIndex
      });
    }

    const totalHeight = (rowSpacing * (visibleItems - 1)) + itemHeight + 8; // Exact height for content

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none md:hidden">
        <div
          className="bg-white/95 backdrop-blur-sm rounded-2xl pointer-events-auto overflow-hidden border border-gray-200 shadow-2xl"
          onTouchStart={handlePeriodTouchStart}
          onTouchMove={handlePeriodTouchMove}
          onTouchEnd={handlePeriodTouchEnd}
          onWheel={handlePeriodWheel}
          style={{
            touchAction: 'none',
            height: `${totalHeight}px`,
            minWidth: '150px',
            perspective: '1000px'
          }}
        >
          {/* 3D Wheel container */}
          <div
            className="flex flex-col items-center justify-center h-full relative"
            style={{
              transformStyle: 'preserve-3d'
            }}
          >
            {circularItems.map(({ period, distance, actualIndex }) => {
              const isActive = distance === 0;
              const absDistance = Math.abs(distance);

              // iOS-style 3D rotation calculation
              const rotationAngle = distance * 12; // Less rotation for subtler 3D effect
              const translateZ = -absDistance * 5; // Less depth
              const translateY = distance * rowSpacing; // Tighter row spacing

              // Smooth scaling: 1 at center, decreasing towards edges (higher minimum)
              const scale = Math.max(0.9, 1 - absDistance * 0.05);

              // Smooth opacity: 1 at center, fading towards edges
              const opacity = Math.max(0.6, 1 - absDistance * 0.15);

              // Font size interpolation: larger at center, smaller at edges (bigger minimum)
              const fontSize = Math.max(14, 17 - absDistance * 1);

              // Font weight: Only selected item gets bold (700), all others are normal (400)
              const fontWeight = isActive ? 700 : 400;

              return (
                <div
                  key={`${period.id}-${distance}`}
                  className="absolute flex items-center justify-center w-full"
                  style={{
                    height: `${itemHeight}px`,
                    transform: `
                      translateY(${translateY}px)
                      translateZ(${translateZ}px)
                      rotateX(${-rotationAngle}deg)
                      scale(${scale})
                    `,
                    opacity,
                    zIndex: 10 - absDistance,
                    transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
                    backgroundColor: isActive ? 'rgb(0, 76, 80)' : 'transparent'
                  }}
                >
                  <span
                    className="text-center px-4 py-1"
                    style={{
                      color: isActive ? '#ffffff' : '#6b7280',
                      fontSize: `${fontSize}px`,
                      fontWeight,
                      transition: 'font-weight 0.4s ease-in-out, font-size 0.25s ease-out, color 0.3s ease-out'
                    }}
                  >
                    {period.display}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleLivePriceUpdate = (event) => {
      const { price } = event.detail || {};
      if (price && showBuySellModal) {
        const assetType = getAssetType(stockData?.ticker);
        const formattedPrice = formatOrderPrice(price, assetType);
        setMarketPrice(formattedPrice);
      }
    };

    window.addEventListener('livePriceUpdate', handleLivePriceUpdate);
    return () => window.removeEventListener('livePriceUpdate', handleLivePriceUpdate);
  }, [showBuySellModal, stockData?.ticker]);

  // Helper function to truncate decimals
  const truncateToDecimals = (num, dec = 2) => {
    const calcDec = Math.pow(10, dec);
    return Math.trunc(num * calcDec) / calcDec;
  };

  // Determine asset type based on ticker
  const getAssetType = (ticker) => {
    if (ticker?.startsWith("C:")) return "forex";
    if (ticker?.startsWith("X:")) return "crypto";
    return "stock";
  };

  // Open Buy/Sell modal and fetch stock data
  const handleBuySellClick = async () => {
    const ticker =
      (isEditMode && editOrder?.stock_symbol) ||
      localStorage.getItem("ticker") ||
      selectedStock ||
      "SPY";
    setBuySellLoading(true);
    setShowBuySellModal(true);
    setShowQuantityPopup(false);
    setForexLotSize(null);
    setForexLotCount(1);

    try {
      const category = ticker.startsWith("C:") ? "Forex" : ticker.startsWith("X:") ? "Crypto" : "Stocks";
      const response = await searchStock({ search: ticker, category });

      if (response?.data?.status === 200) {
        const data = response.data.data;
        setStockData(data);
        const price = data?.min?.c || data?.prevDay?.c || 0;
        const assetType = getAssetType(data?.ticker);
        const formattedPrice = formatOrderPrice(price, assetType);
        if (isEditMode) {
          const editPrice = Number(editOrder.order_price);
          const editFormatted = Number.isFinite(editPrice)
            ? formatOrderPrice(editPrice, assetType)
            : formattedPrice;
          const pt = Number(editOrder.postion_type);
          const tab = pt === 3 ? "stopLoss" : pt === 1 ? "market" : "limit";
          setLimitPrice(tab === "stopLoss" ? formattedPrice : editFormatted);
          setLossPrice(tab === "stopLoss" ? editFormatted : formattedPrice);
          setMarketPrice(formattedPrice);
          setQuantity(Number(editOrder.order_quantity) || 0);
          setActiveOrderTab(tab);
          setTradeType(
            Number(editOrder.order_type) === 2 ||
              Number(editOrder.order_type) === 3
              ? 2
              : 1
          );
        } else {
          setLimitPrice(formattedPrice);
          setLossPrice(formattedPrice);
          setMarketPrice(formattedPrice); 
          const guided = guideActive && guideStep === 3;
          if (guided) {
            setQuantity(1);
            setActiveOrderTab("market");
            setTradeType(1);
            if (!guideAutofillDone()) {
              markGuideAutofillDone();
              setShowGuideTooltip(true);
            }
          } else {
            setQuantity(0);
            setActiveOrderTab("limit");
            setTradeType(1);
          }
          // order_started: the ticket sheet has opened on this instrument.
          if (startedFor.current !== data?.ticker) {
            startedFor.current = data?.ticker;
            trackOrderStarted({
              ticker: data?.ticker,
              orderTab: guided ? "market" : "limit",
              game: league?.game || null,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
      toast.error("Price data is unavailable. Try again.");
    } finally {
      setBuySellLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode && !showBuySellModal) {
      handleBuySellClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    if (!showBuySellModal) return;
    if (!selectedStock) return;
    if (stockData?.ticker && stockData.ticker.toUpperCase() === String(selectedStock).toUpperCase()) {
      return;
    }
    handleBuySellClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock, showBuySellModal]);

  useEffect(() => {
    if (guideActive && guideStep === 3 && !showBuySellModal) {
      handleBuySellClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideStep, guideActive]);

  // Closing the sheet ends that ticket; opening it again is a new order_started.
  useEffect(() => {
    if (!showBuySellModal) startedFor.current = null;
  }, [showBuySellModal]);


  // Handle trade type change (Buy/Sell)
  const handleTradeTypeChange = (e) => {
    setTradeType(e.target.value === "Buy" ? 1 : 2);
  };

  // Handle order tab click (limit, market, stopLoss)
  const handleOrderTabClick = (tab) => {
    setActiveOrderTab(tab);
  };

  // Increment quantity
  const incrementQuantity = () => {
    setQuantity(prev => Number(prev) + 1);
  };

  // Decrement quantity
  const decrementQuantity = () => {
    if (quantity > 0) {
      setQuantity(prev => Number(prev) - 1);
    }
  };

 const determineValue = () => {
    if (activeOrderTab === "market") {
      return marketPrice || stockData?.min?.c || stockData?.prevDay?.c || 0;
    }
    return activeOrderTab === "stopLoss" ? lossPrice : limitPrice;
  };

  const forexSelectLot = (units) => {
    setForexLotSize(units);
    setForexLotCount(1);
    setQuantity(units);
  };
  const forexIncrement = () => {
    if (!forexLotSize) return;
    const n = forexLotCount + 1;
    setForexLotCount(n);
    setQuantity(forexLotSize * n);
  };
  const forexDecrement = () => {
    if (!forexLotSize) return;
    const n = Math.max(1, forexLotCount - 1);
    setForexLotCount(n);
    setQuantity(forexLotSize * n);
  };
  const forexClear = () => {
    setForexLotSize(null);
    setForexLotCount(1);
    setQuantity(0);
  };

  const handlePlaceOrder = async () => {
    if (!stockData) {
      toast.error("Price data is unavailable. Try again.");
      return;
    }
    if (!quantity || quantity === 0) {
      toast.error("Please enter quantity");
      return;
    }
    // A limit or stop at 0 (or below) is not an order; the backend refuses it
    // too. Market orders take the live price.
    if (activeOrderTab !== "market" && !(Number(determineValue()) > 0)) {
      toast.error("Enter a price above 0.");
      return;
    }

    const leagueBlock = league?.game
      ? leagueTradeBlockReason(league.game, stockData?.ticker)
      : null;
    if (leagueBlock) {
      toast.error(orderErrorText(leagueBlock));
      return;
    }

    // Client rule 3: "Recheck the position quantity when the order is
    // submitted." Same guard, same wording as the desktop ticket — see
    // InlineBuySellPanel for why it runs only on an order that closes something.
    const closesLong = position?.direction === "long" && Number(tradeType) === 2;
    const closesShort = position?.direction === "short" && Number(tradeType) === 1;
    if (!isEditMode && (closesLong || closesShort)) {
      const fresh = await refreshPosition();
      const stillHeld = fresh?.held ?? 0;
      // Measured against the FULL net position, not the position minus resting
      // closing orders. Client's own case: "Long 500 with resting sell limit
      // 500. Manually sell 300. Result: hold 200, limit clamped to 200." A guard
      // that subtracted the resting 500 would have refused that 300.
      // Over-closing is prevented by the backend clamping resting orders inside
      // the same transaction as the fill, not by shrinking what may be typed.
      if (Number(quantity) > stillHeld) {
        toast.error(
          stillHeld > 0
            ? `You can close ${stillHeld} right now. Your position changed.`
            : "That position is already closed."
        );
        return;
      }
    }

    setBuySellLoading(true);
    const assetType = getAssetType(stockData?.ticker);

    const data = {
      stock_symbol: stockData.ticker,
      asset_type: assetType,
      order_type: tradeType,
      order_quantity: quantity,
      order_price: determineValue(),
      postion_type: activeOrderTab === "stopLoss" ? 3 : activeOrderTab === "limit" ? 2 : 1,
    };
    if (isEditMode) {
      data.id = editOrder.id;
    }
    if (league?.gameId) {
      data.game_id = league.gameId;
    }
    const ticket = { ticker: stockData.ticker, orderTab: activeOrderTab, game: league?.game || null };
    if (!isEditMode) {
      if (startedFor.current !== stockData.ticker) {
        startedFor.current = stockData.ticker;
        trackOrderStarted(ticket);
      }
      data.client_order_id = trackOrderSubmitted(ticket);
    }

    try {
      const response = await createOrder(data);
      if (response?.data?.status === 200) {
        // Client: "Order filled." for confirmed fills and "Order placed." for
        // accepted orders awaiting execution.
        const filled = !isEditMode && isConfirmedFill(response.data);
        toast.success(isEditMode ? "Order updated." : filled ? "Order filled." : "Order placed.");
        if (filled) trackTradeConfirmationViewed(response.data, ticket);
        setShowBuySellModal(false);
        setQuantity(0);
        if (isEditMode) {
          onEditOrderDone && onEditOrderDone();
        }
        if (guideActive && guideStep === 3) {
          advanceStep(4);
          navigate("/portfolio");
        }
      } else {
        toast.error(orderErrorText(response?.data?.message));
      }
    } catch (error) {
      if (error?.response) {
       toast.error(orderErrorText(error.response?.data?.message));
      } else {
        toast.error(
          "We could not confirm this order. Check your portfolio before trying again."
        );
      }
      console.error("Error placing order:", error);
    } finally {
      setBuySellLoading(false);
    }
  };

  return (
    <>
      <style>{slideUpStyles}</style>

      <div
        className="mobile-chart-toolbar md:hidden fixed bottom-16 left-0 right-0 z-40 px-3 bg-gray-100 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
        style={{ display: isSidebarOpen ? "none" : undefined }}
      >
      <div className="flex items-center gap-2 justify-between !w-full">
        <div
          id="chart-guide-asset-wheel"
          className="stock-scroll-picker relative select-none w-28 overflow-hidden rounded-xl"
          onClick={() => navigate('/search')}
          onTouchStart={(e) => {
            e.currentTarget.dataset.touchStartY = e.touches[0].clientY;
            e.currentTarget.dataset.touchStartTime = Date.now();
          }}
          onTouchMove={(e) => {
            const startY = parseFloat(e.currentTarget.dataset.touchStartY || 0);
            const currentY = e.touches[0].clientY;
            const deltaY = Math.abs(currentY - startY);
            if (deltaY > 5 && !showStockWheelPicker) {
              e.preventDefault();
              setIsScrollingStock(false);
              clearTimeout(stockScrollTimer.current);
              setStockInitialTouchY(currentY);
              setShowStockWheelPicker(true);
            }
          }}
          onWheel={(e) => { e.preventDefault(); setIsScrollingStock(false); clearTimeout(stockScrollTimer.current); setStockInitialTouchY(null); setShowStockWheelPicker(true); }}
          style={{
            touchAction: 'none',
            width: '90px',
            height: '52px',
            perspective: '400px',
            perspectiveOrigin: '50% 50%',
            flex: '0 0 90px',
          }}
        >
          <div
            className="relative h-full flex items-center justify-center"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {(() => {
              let currentIdx;
              if (wheelScrollState && showStockWheelPicker) {
                currentIdx = wheelScrollState.currentIndex;
              } else if (isScrollingStock) {
                currentIdx = stockIndex;
              } else if (desiredStockSymbolRef.current) {
                const foundIdx = stocks.findIndex(s => s.symbol === desiredStockSymbolRef.current);
                currentIdx = foundIdx >= 0 ? foundIdx : stockIndex;
              } else {
                currentIdx = stockIndex;
              }
              const fractionalOffset = (wheelScrollState && showStockWheelPicker)
                ? wheelScrollState.fractionalOffset || 0
                : 0;

              const items = [-1, 0, 1].map(offset => {
                const idx = ((currentIdx + offset) % stocks.length + stocks.length) % stocks.length;
                const stock = stocks[idx];
                if (!stock) return null;

                const offsetFromCenter = offset - fractionalOffset;
                const absOffset = Math.abs(offsetFromCenter);

                const angle = offsetFromCenter * 35; // rotation angle
                const radians = (angle * Math.PI) / 180;
                const translateY = Math.sin(radians) * 25;
                const translateZ = (Math.cos(radians) - 1) * 15;
                const scale = 1 - absOffset * 0.15;
                const opacity = Math.max(0.3, 1 - absOffset * 0.5);
                const isCenter = absOffset < 0.5;

                return (
                  <div
                    key={`stock-${idx}-${offset}`}
                    className="absolute w-full flex items-center justify-center pointer-events-none"
                    style={{
                      height: '18px',
                      transform: `translateY(${translateY}px) translateZ(${translateZ}px) rotateX(${-angle}deg) scale(${scale})`,
                      opacity,
                      zIndex: Math.round(10 - absOffset * 5),
                      transition: 'none',
                    }}
                  >
                    <span className={`font-bold transition-all duration-100 ${
                      isCenter ? 'text-[rgb(0,76,80)] text-base' : 'text-gray-400 text-base'
                    }`}>
                      {pairSymbol(stock.symbol)}
                    </span>
                  </div>
                );
              });

              return items;
            })()}
          </div>
        </div>

        <div
          id="chart-guide-timeframe-wheel"
          className="period-scroll-picker relative select-none overflow-hidden rounded-xl"
          onTouchStart={(e) => {
            e.currentTarget.dataset.touchStartY = e.touches[0].clientY;
            e.currentTarget.dataset.touchStartTime = Date.now();
          }}
          onTouchMove={(e) => {
            const startY = parseFloat(e.currentTarget.dataset.touchStartY || 0);
            const currentY = e.touches[0].clientY;
            const deltaY = Math.abs(currentY - startY);
            if (deltaY > 5 && !showPeriodWheelPicker) {
              e.preventDefault();
              setIsScrollingPeriod(false);
              clearTimeout(periodScrollTimer.current);
              setPeriodInitialTouchY(currentY);
              setShowPeriodWheelPicker(true);
            }
          }}
          onWheel={(e) => { e.preventDefault(); setIsScrollingPeriod(false); clearTimeout(periodScrollTimer.current); setPeriodInitialTouchY(null); setShowPeriodWheelPicker(true); }}
          style={{
            touchAction: 'none',
            height: '52px',
            width: '80px',
            perspective: '400px',
            perspectiveOrigin: '50% 50%',
            flex: 1,
          }}
        >
          <div
            className="relative h-full flex items-center justify-center"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {(() => {
               let currentIdx;
              if (periodWheelScrollState && showPeriodWheelPicker) {
                currentIdx = periodWheelScrollState.currentIndex;
              } else if (desiredPeriodIdRef.current !== null) {
                const foundIdx = quickPeriods.findIndex(p => p.id === desiredPeriodIdRef.current);
                currentIdx = foundIdx >= 0 ? foundIdx : periodIndex;
              } else {
                currentIdx = periodIndex;
              }
              const fractionalOffset = (periodWheelScrollState && showPeriodWheelPicker)
                ? periodWheelScrollState.fractionalOffset || 0
                : 0;

              const items = [-1, 0, 1].map(offset => {
                const idx = ((currentIdx + offset) % quickPeriods.length + quickPeriods.length) % quickPeriods.length;
                const period = quickPeriods[idx];
                if (!period) return null;

                const offsetFromCenter = offset - fractionalOffset;
                const absOffset = Math.abs(offsetFromCenter);

                const angle = offsetFromCenter * 35;
                const radians = (angle * Math.PI) / 180;
                const translateY = Math.sin(radians) * 25;
                const translateZ = (Math.cos(radians) - 1) * 15;
                const scale = 1 - absOffset * 0.15;
                const opacity = Math.max(0.3, 1 - absOffset * 0.5);
                const isCenter = absOffset < 0.5;

                return (
                  <div
                    key={`period-${idx}-${offset}`}
                    className="absolute w-full flex items-center justify-center pointer-events-none"
                    style={{
                      height: '18px',
                      transform: `translateY(${translateY}px) translateZ(${translateZ}px) rotateX(${-angle}deg) scale(${scale})`,
                      opacity,
                      zIndex: Math.round(10 - absOffset * 5),
                      transition: 'none',
                    }}
                  >
                    <span className={`font-bold transition-all duration-100 ${
                      isCenter ? 'text-[rgb(0,76,80)] text-base' : 'text-gray-400 text-base'
                    }`}>
                      {period.shortDisplay}
                    </span>
                  </div>
                );
              });

              return items;
            })()}
          </div>
        </div>

        {/* Icons Section */}

          <button
            id="chart-guide-drawing-tools"
            onClick={onPencilClick}
            className=" text-black hover:text-primaryColor transition-colors flex-1"
            title="Drawing Tools"
          >
            <FontAwesomeIcon icon={faPencil} className="w-8 h-8" />
          </button>

          <button
            id="chart-guide-trade-arrows"
            onClick={handleBuySellClick}
            className=" text-black hover:text-primaryColor transition-colors flex-1"
            title="Buy/Sell"
          >
            <img src={upDown} alt="Buy/Sell" className="w-8 h-8 mx-auto" />
          </button>

          <button
            id="chart-guide-settings"
            onClick={() => setShowToolsModal(true)}
            className="text-black hover:text-primaryColor transition-colors flex-1"
            title="More Options"
          >
            <FontAwesomeIcon icon={faEllipsis} className="w-8 h-8" />
          </button>

          <button
            id="chart-guide-share"
            onClick={onDirectShareChart}
            className=" text-black hover:text-primaryColor transition-colors flex-1"
            title="Share Chart"
          >
            <FontAwesomeIcon icon={faPaperPlane} className="w-8 h-8" />
          </button>
        </div>
      </div>

      {renderStockOverlay()}
      {renderPeriodOverlay()}

      {showStockWheelPicker && (
        <WheelPicker3D
          items={stocks}
          selectedIndex={stockIndex}
          onSelect={handleWheelStockSelect}
          onClose={() => { setShowStockWheelPicker(false); setWheelScrollState(null); setStockInitialTouchY(null); }}
          onScrollChange={setWheelScrollState}
          width={160}
          height={200}
          itemHeight={48}
          visibleItems={7}
          autoCloseDelay={600}
          initialTouchY={stockInitialTouchY}
          renderItem={(stock, index, isSelected) => (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 ${
              isSelected ? 'transparent' : ''
            }`}>
              <span className={`font-bold ${isSelected ? ' text-primaryColor text-2xl ' : 'text-gray-500 text-base'}`}>
                {pairSymbol(stock.symbol)}
              </span>
            </div>
          )}
        />
      )}

      {/* 3D Wheel Picker for Period Selection - No title, auto-close on scroll stop */}
      {showPeriodWheelPicker && (
        <WheelPicker3D
          items={quickPeriods}
          selectedIndex={periodIndex}
          onSelect={handleWheelPeriodSelect}
          onClose={() => { setShowPeriodWheelPicker(false); setPeriodWheelScrollState(null); setPeriodInitialTouchY(null); }}
          onScrollChange={setPeriodWheelScrollState}
          width={200}
          height={200}
          itemHeight={48}
          visibleItems={7}
          autoCloseDelay={600}
          initialTouchY={periodInitialTouchY}
          renderItem={(period, index, isSelected) => (
            <div className={`flex items-center justify-center px-2 py-2 rounded-xl transition-all duration-200 ${
              isSelected ? 'transparent' : ''
            }`}>
              <span className={`font-bold whitespace-nowrap ${isSelected ? ' text-primaryColor text-2xl' : 'text-gray-500 text-base'}`}>
                {period.display}
              </span>
            </div>
          )}
        />
      )}

      {/* Tools Modal */}
      {showToolsModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70"
          data-no-chart-tap="true"
          onClick={() => setShowToolsModal(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl border-t border-x border-gray-700"
            onClick={(e) => e.stopPropagation()}
            style={getModalSwipeStyle('tools')}
          >
            {/* Handle bar - draggable to close */}
            <div
              className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
              {...createModalSwipeHandlers(setShowToolsModal, 'tools')}
            >
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-primaryColor text-lg font-semibold">Tools</h3>
              <button
                onClick={() => setShowToolsModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Tools Grid */}
            <div className="grid grid-cols-3 gap-3">
              {/* Overlays */}
              <button
                onClick={handleOpenOverlaysModal}
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-lg  transition-colors"
              >
                <FontAwesomeIcon icon={faLayerGroup} className="w-6 h-6 text-primaryColor mb-2" />
                <span className="text-primaryColor  text-xs">Overlays</span>
              </button>

              {/* Indicators */}
              <button
                onClick={() => setShowIndicatorsModal(true)}
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-lg  transition-colors"
              >
                <FontAwesomeIcon icon={faChartLine} className="w-6 h-6 text-primaryColor mb-2" />
                <span className="text-primaryColor  text-xs">Indicators</span>
              </button>

              {/* Chart Type */}
              <button
                onClick={() => setShowChartTypeModal(true)}
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-lg  transition-colors"
              >
                <svg className="w-6 h-6 text-primaryColor mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="12" width="3" height="8" rx="0.5" />
                  <rect x="8" y="6" width="3" height="14" rx="0.5" />
                  <rect x="13" y="9" width="3" height="11" rx="0.5" />
                  <rect x="18" y="4" width="3" height="16" rx="0.5" />
                </svg>
                <span className="text-primaryColor  text-xs">Chart Type</span>
              </button>

              {/* Volume Toggle */}
              <button
                onClick={() => {
                  if (setSetting) {
                    setSetting(prev => ({
                      ...prev,
                      chartOverlay: prev.chartOverlay === "on" ? "off" : "on"
                    }));
                  }
                }}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-colors ${
                  setting?.chartOverlay === "on"
                    ? "border border-primaryColor bg-primaryColor"
                    : "border border-gray-300 "
                }`}
              >
                <svg className={`w-6 h-6 mb-2 ${setting?.chartOverlay === "on" ? "text-white" : "text-primaryColor"}`} fill="currentColor" viewBox="0 0 24 24">
                  <rect x="4" y="14" width="4" height="6" rx="0.5" />
                  <rect x="10" y="10" width="4" height="10" rx="0.5" />
                  <rect x="16" y="6" width="4" height="14" rx="0.5" />
                </svg>
                <span className={`text-xs ${setting?.chartOverlay === "on" ? "text-white" : "text-primaryColor"}`}>
                  Volume {setting?.chartOverlay === "on" ? "ON" : "OFF"}
                </span>
              </button>

              <button
                onClick={() => {
                  if (setSetting) {
                    setSetting(prev => ({
                      ...prev,
                      enablePrice: !prev.enablePrice
                    }));
                  }
                }}
                className={`flex flex-col items-center justify-center p-3 rounded-lg transition-colors ${
                  setting?.enablePrice
                    ? "border border-primaryColor bg-primaryColor"
                    : "border border-gray-300 "
                }`}
              >
                <FontAwesomeIcon
                  icon={faTags}
                  className={`w-6 h-6 mb-2 ${setting?.enablePrice ? "text-white" : "text-primaryColor"}`}
                />
                <span className={`text-xs ${setting?.enablePrice ? "text-white" : "text-primaryColor"}`}>
                  Price Labels {setting?.enablePrice ? "ON" : "OFF"}
                </span>
              </button>

              <button
                onClick={() => {
                  if (onAlertClick) {
                    onAlertClick();
                  }
                }}
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-lg  transition-colors"
              >
                <FontAwesomeIcon icon={faBell} className="w-6 h-6 text-primaryColor mb-2" />
                <span className="text-primaryColor  text-xs">Alerts</span>
              </button>

              <button
                onClick={() => {
                  if (onWatchlistClick) {
                    onWatchlistClick();
                  }
                }}
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-lg  transition-colors"
              >
                <svg className="w-6 h-6 text-primaryColor mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v8M8 12h8" />
                </svg>
                <span className="text-primaryColor  text-xs">Watchlist</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showChartTypeModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
          data-no-chart-tap="true"
          onClick={() => setShowChartTypeModal(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl border-t border-x border-gray-700"
            onClick={(e) => e.stopPropagation()}
            style={getModalSwipeStyle('chartType')}
          >
            <div
              className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
              {...createModalSwipeHandlers(setShowChartTypeModal, 'chartType')}
            >
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-primaryColor text-lg font-semibold">Chart Type</h3>
              <button
                onClick={() => setShowChartTypeModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              {chartTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => {
                    if (setSetting) {
                      setSetting(prev => ({
                        ...prev,
                        chartType: type.value
                      }));
                    }
                    setShowChartTypeModal(false);
                  }}
                  className={`w-full flex items-center gap-4 p-4 rounded-lg transition-colors ${
                    setting?.chartType === type.value
                      ? "bg-primaryColor border border-primaryColor/70 text-primaryColor"
                      : "bg-white border border-gray-300 hover:bg-gray-100 text-primaryColor"
                  }`}
                >
                  {type.value === "candlestick" && (
                    <svg className={`w-8 h-8 ${setting?.chartType === type.value ? "text-white" : "text-primaryColor"}`} fill="currentColor" viewBox="0 0 24 24">
                      <rect x="4" y="8" width="4" height="8" rx="0.5" />
                      <line x1="6" y1="4" x2="6" y2="8" stroke="currentColor" strokeWidth="1.5" />
                      <line x1="6" y1="16" x2="6" y2="20" stroke="currentColor" strokeWidth="1.5" />
                      <rect x="10" y="6" width="4" height="10" rx="0.5" />
                      <line x1="12" y1="2" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5" />
                      <line x1="12" y1="16" x2="12" y2="22" stroke="currentColor" strokeWidth="1.5" />
                      <rect x="16" y="10" width="4" height="6" rx="0.5" />
                      <line x1="18" y1="6" x2="18" y2="10" stroke="currentColor" strokeWidth="1.5" />
                      <line x1="18" y1="16" x2="18" y2="20" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  )}
                  {type.value === "ohlc" && (
                    <svg className={`w-8 h-8 ${setting?.chartType === type.value ? "text-white" : "text-primaryColor"}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="6" y1="4" x2="6" y2="20" />
                      <line x1="3" y1="8" x2="6" y2="8" />
                      <line x1="6" y1="16" x2="9" y2="16" />
                      <line x1="12" y1="6" x2="12" y2="18" />
                      <line x1="9" y1="10" x2="12" y2="10" />
                      <line x1="12" y1="14" x2="15" y2="14" />
                      <line x1="18" y1="8" x2="18" y2="16" />
                      <line x1="15" y1="10" x2="18" y2="10" />
                      <line x1="18" y1="14" x2="21" y2="14" />
                    </svg>
                  )}
                  {type.value === "solid" && (
                    <svg className={`w-8 h-8 ${setting?.chartType === type.value ? "text-white" : "text-primaryColor"}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M2 16 L6 12 L10 14 L14 8 L18 10 L22 6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  <span className={`text-base font-medium ${setting?.chartType === type.value ? "text-white" : "text-primaryColor"}`}>
                    {type.label}
                  </span>
                  {setting?.chartType === type.value && (
                    <div className="ml-auto">
                      <svg className="w-5 h-5 text-primaryColor" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showOverlaysModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
          data-no-chart-tap="true"
          onClick={() => setShowOverlaysModal(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl border-t border-x border-gray-700 overflow-x-hidden"
            onClick={(e) => {
              e.stopPropagation();
              // "a user can click outside the options" — the field button and
              // the list stop propagation, so neither closes itself.
              setOpenOverlayPicker(null);
            }}
            style={getModalSwipeStyle('overlays')}
          >
            <div
              className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
              {...createModalSwipeHandlers(setShowOverlaysModal, 'overlays')}
            >
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-primaryColor text-lg font-semibold">Overlays</h3>
              <button
                onClick={() => setShowOverlaysModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-2 mb-2">
              <span className="flex-1 text-black text-xs font-medium">Overlay</span>
              <span className="w-28 text-black text-xs font-medium">Parameters</span>
            </div>

            <div className="space-y-3 max-h-[350px] overflow-y-auto overflow-x-hidden">
              {overlayRows.map((row, index) => (
                <div key={`overlay-row-${index}`}>
                  <div className="flex gap-2">
                    <MobileSelectField
                      value={row.selectValue}
                      options={overlayOptions}
                      placeholder="Select overlay"
                      onToggle={() => setOpenOverlayPicker(openOverlayPicker === index ? null : index)}
                      className="flex-1 min-w-0 bg-white text-primaryColor text-sm rounded-lg px-2 py-2.5 border border-gray-300 focus:border-primaryColor focus:outline-none appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 4px center',
                        backgroundSize: '14px',
                        paddingRight: '20px'
                      }}
                    />

                    <input
                      type="text"
                      value={row.inputValue}
                      onChange={(e) => handleOverlayInputChange(index, e.target.value)}
                      placeholder="Parameters"
                      className="w-28 bg-white text-primaryColor text-sm rounded-lg px-2 py-2.5 border border-gray-300 focus:border-primaryColor focus:outline-none placeholder-primaryColor"
                    />
                  </div>

                  {openOverlayPicker === index && (
                    <MobileSelectList
                      options={overlayOptions}
                      itemClassName="text-sm"
                      onSelect={(value) => {
                        handleOverlaySelectChange(index, value);
                        setOpenOverlayPicker(null);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleOverlaySubmit}
                className="flex-1 bg-primaryColor hover:bg-primaryColorDark text-white font-medium py-2.5 rounded-lg transition-colors"
              >
                Update
              </button>
              <button
                onClick={handleOverlayClearAll}
                className="flex-1 bg-white  text-primaryColor font-medium py-2.5 rounded-lg border border-primaryColor transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {showIndicatorsModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
          data-no-chart-tap="true"
          onClick={() => setShowIndicatorsModal(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl border-t border-x border-gray-700 overflow-x-hidden"
            onClick={(e) => {
              e.stopPropagation();
              // "a user can click outside the options" — the field button and
              // the list stop propagation, so neither closes itself.
              setOpenIndicatorPicker(null);
            }}
            style={getModalSwipeStyle('indicators')}
          >
            <div
              className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
              {...createModalSwipeHandlers(setShowIndicatorsModal, 'indicators')}
            >
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            {/* Client, 2: "Add up to 2 indicators" is the SUBTITLE of the modal
                header, sitting directly under "Indicators" — not a separate line
                further down the body. Desktop does the same through
                SimpleModal's `subtitle` slot. */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-primaryColor text-lg font-semibold">Indicators</h3>
                <p className="mt-0.5 text-secondary text-xs">Add up to 2 indicators.</p>
              </div>
              <button
                onClick={() => setShowIndicatorsModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-1.5 mb-2">
              <span className="flex-1 text-black text-xs font-medium">Indicator</span>
              <span className="w-24 text-black text-xs font-medium">Parameters</span>
              <span className="w-16 text-black text-xs font-medium">Position</span>
            </div>

            <div className="space-y-3 max-h-[350px] overflow-y-auto overflow-x-hidden">
              {visibleIndicatorRows.map((row, index) => (
                <div key={`indicator-row-${index}`}>
                  <div className="flex gap-1.5">
                    <MobileSelectField
                      value={row.selectValue}
                      options={indicatorOptions}
                      placeholder="Select indicator"
                      onToggle={() => setOpenIndicatorPicker(openIndicatorPicker === index ? null : index)}
                      className="flex-1 min-w-0 bg-white text-primaryColor text-xs rounded-lg px-1.5 py-2.5 border border-gray-300 focus:border-primaryColor focus:outline-none appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 2px center',
                        backgroundSize: '12px',
                        paddingRight: '16px'
                      }}
                    />

                    <input
                      type="text"
                      value={row.inputValue}
                      onChange={(e) => handleIndicatorInputChange(index, e.target.value)}
                      placeholder="Params"
                      className="w-24 bg-white text-primaryColor text-xs rounded-lg px-2 py-2.5 border border-gray-300 focus:border-primaryColor focus:outline-none placeholder-primaryColor"
                    />

                    <select
                      value={row.selectPosValue || "above"}
                      onChange={(e) => handleIndicatorPositionChange(index, e.target.value)}
                      className="w-16 bg-white text-primaryColor text-xs rounded-lg px-1 py-2.5 border border-gray-300 focus:border-primaryColor focus:outline-none appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 2px center',
                        backgroundSize: '10px',
                        paddingRight: '14px'
                      }}
                    >
                      <option value="above" className="bg-white text-primaryColor">Above</option>
                      <option value="below" className="bg-white text-primaryColor">Below</option>
                    </select>
                  </div>

                  {openIndicatorPicker === index && (
                    <MobileSelectList
                      /* Same alphabetical order the <select> rendered. */
                      options={[...indicatorOptions].sort((a, b) => a.label.localeCompare(b.label))}
                      itemClassName="text-xs"
                      onSelect={(value) => {
                        handleIndicatorSelectChange(index, value);
                        setOpenIndicatorPicker(null);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleIndicatorSubmit}
                disabled={!setting?.period || !setting?.symbol}
                className="flex-1 bg-primaryColor hover:bg-primaryColorDark text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update
              </button>
              <button
                onClick={handleIndicatorClearAll}
                className="flex-1 bg-white  text-primaryColor font-medium py-2.5 rounded-lg border border-primaryColor transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {showSavedChartsModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
          data-no-chart-tap="true"
          onClick={() => setShowSavedChartsModal(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl border-t border-x border-gray-700 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={getModalSwipeStyle('savedCharts')}
          >
            <div
              className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
              {...createModalSwipeHandlers(setShowSavedChartsModal, 'savedCharts')}
            >
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-primaryColor text-lg font-semibold">Saved Charts</h3>
              <button
                onClick={() => setShowSavedChartsModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <button
              onClick={() => {
                if (onSaveChartClick) {
                  onSaveChartClick();
                }
              }}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-3 rounded-lg transition-colors mb-4 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Save Current Chart
            </button>

            {savedCharts && savedCharts.length > 0 ? (
              <div className="space-y-3">
                {savedCharts.map((chart, index) => (
                  <div
                    key={index}
                    className="bg-[#252b3d] rounded-lg border border-gray-700"
                  >
                    <div
                      onClick={() => {
                        if (onSavedChartClick) {
                          onSavedChartClick(chart);
                        }
                        setShowSavedChartsModal(false);
                      }}
                      className="cursor-pointer"
                    >
                      {chart.image && (
                        <img
                          src={chart.image}
                          alt="saved chart"
                          className="w-full h-40 object-cover rounded-t-lg"
                        />
                      )}
                    </div>
                    <div className="p-3 flex justify-between items-start relative">
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">
                          {chart.stock_symbol || chart.symbol || 'Chart'}
                        </p>
                        {chart.created_at && (
                          <p className="text-gray-400 text-xs mt-1">
                            {new Date(chart.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>

                      <div className="relative z-20">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(index);
                          }}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <FontAwesomeIcon icon={faEllipsisV} className="w-3 h-3" />
                        </button>

                        {dropdownStates[index] && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onDeleteChart) {
                                  onDeleteChart(chart);
                                }
                                toggleDropdown(index);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2 rounded-t-lg"
                            >
                              <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                              Delete Chart
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onShareChartAsPost) {
                                  onShareChartAsPost(chart);
                                }
                                toggleDropdown(index);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-primaryColor hover:bg-gray-100 flex items-center gap-2 rounded-b-lg"
                            >
                              <FontAwesomeIcon icon={faShare} className="w-3 h-3" />
                              Share Chart as Post
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400">No saved charts yet</p>
                <p className="text-gray-500 text-sm mt-2">Save your first chart to see it here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showBuySellModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70"
          data-no-chart-tap="true"
          onClick={closeBuySellModal}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-w-lg p-4 shadow-2xl max-h-[85vh] overflow-visible relative"
            onClick={(e) => {
              e.stopPropagation();
              if (showQuantityPopup) {
                setShowQuantityPopup(false);
              }
            }}
            style={getModalSwipeStyle('buySell', { minHeight: '460px' })}
          >
            {guideActive && guideStep === 3 && showGuideTooltip && (() => {
              const persona = localStorage.getItem("qushi_onboarding_persona");
              const title = persona === "basics" ? "Your practice order is ready" : "Your order is ready";
              const subtitle = persona === "basics"
                ? "We filled this in for you. Tap Place Order to start building reps."
                : "We filled this in for you. Tap Place Order to buy with virtual money.";
              return (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginBottom: 16,
                    background: "#004c50",
                    color: "#fff",
                    borderRadius: 12,
                    padding: "14px 18px",
                    textAlign: "center",
                    width: "85%",
                    maxWidth: 360,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                    zIndex: 70,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                    {title}
                  </p>
                  <p style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.4 }}>
                    {subtitle}
                  </p>
                  <div
                    style={{
                      position: "absolute",
                      bottom: -8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 0,
                      height: 0,
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderTop: "8px solid #1B4D3E",
                    }}
                  />
                </div>
              );
            })()}

            <div
              className="flex justify-center mb-3 cursor-grab py-2 -mt-2"
              {...createModalSwipeHandlers(setShowBuySellModal, 'buySell')}
            >
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-gray-800 text-lg font-semibold">
                {pairSymbol(currentStock || stockData?.ticker) || "Trade"}
              </h3>
              <button
                onClick={closeBuySellModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {league?.gameId && league?.title && (
              <p className="mb-4 truncate whitespace-nowrap text-left text-xs text-secondary">
                League portfolio ·{" "}
                {league.title.length > 17
                  ? `${league.title.slice(0, 17)}…`
                  : league.title}
              </p>
            )}

            <div className="space-y-4">
                <div>
                  <p className="text-gray-800 mb-2 font-medium">Trade Type</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setTradeType(2)}
                      className={`flex-1 h-12 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-1 ${
                        tradeType === 2
                          ? 'bg-[#fe5b5e] text-white '
                          : 'text-gray-800 bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      <span className="text-2xl">
                      <FontAwesomeIcon icon={faArrowDown} className={`w-6 h-6 -rotate-45 ${tradeType === 2 ? 'text-white' : 'text-gray-800'}`} />
                      </span>
                      Sell
                    </button>
                    <button
                      onClick={() => setTradeType(1)}
                      className={`flex-1 h-12 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-1 ${
                        tradeType === 1
                          ? 'bg-[#01cea7] text-white '
                          : 'text-gray-800 bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      <span className="text-2xl">
                      <FontAwesomeIcon icon={faArrowUp} className={`w-6 h-6 rotate-45 ${tradeType === 1 ? 'text-white' : 'text-gray-800'}`} />
                      </span>
                      Buy
                    </button>
                  </div>
                </div>

                <div className="flex justify-center">
                  <div
                    className={`cursor-pointer py-2 px-4 rounded-l-lg border border-[#004c50] flex-1 text-center ${
                      activeOrderTab === "limit" ? "bg-[#004c50] text-white" : "bg-white text-[#004c50]"
                    }`}
                    onClick={() => handleOrderTabClick("limit")}
                  >
                    Limit
                  </div>
                  <div
                    className={`cursor-pointer py-2 px-4 border border-[#004c50] flex-1 text-center ${
                      activeOrderTab === "market" ? "bg-[#004c50] text-white" : "bg-white text-[#004c50]"
                    }`}
                    onClick={() => handleOrderTabClick("market")}
                  >
                    Market
                  </div>
                  <div
                    className={`cursor-pointer py-2 px-4 rounded-r-lg border border-[#004c50] flex-1 text-center ${
                      activeOrderTab === "stopLoss" ? "bg-[#004c50] text-white" : "bg-white text-[#004c50]"
                    }`}
                    onClick={() => handleOrderTabClick("stopLoss")}
                  >
                    Stop Loss
                  </div>
                </div>

                <div className="flex items-baseline justify-between">
                  <p className="text-gray-800 font-medium text-sm">Free Cash</p>
                  <p className="text-gray-800 font-normal">
                    {freeCash === null ? "—" : formatCash(freeCash)}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="relative">
                    <p className="text-gray-800 mb-2 font-medium text-sm">{quantityLabel(getAssetType(stockData?.ticker))}</p>
                    <div
                      className="flex items-center justify-between px-3 py-2 bg-gray-200 rounded-lg cursor-pointer border border-transparent hover:border-[#004c50] transition-colors"
                      onClick={() => setShowQuantityPopup(!showQuantityPopup)}
                    >
                      <span className="text-gray-800 font-medium flex-1 text-center">{quantity}</span>
                      <span className="text-gray-500 text-xs">+/-</span>
                    </div>

                    {showQuantityPopup && (
                      <div
                        className="absolute left-24 bg-[#1a1a2e] rounded-xl p-4 shadow-2xl z-50 w-40 bottom-full mb-2 overflow-y-auto h-52"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Above the forex split — see InlineBuySellPanel. A
                            forex ticket renders ForexLotKeypad, so chips nested
                            in the other arm never appeared for forex at all. */}
                        <PositionCloseActions
                          position={position}
                          onSelect={applyPositionAction}
                        />
                        {getAssetType(stockData?.ticker) === "forex" ? (
                          <ForexLotKeypad
                            lotSize={forexLotSize}
                            onSelectLot={forexSelectLot}
                            onIncrement={forexIncrement}
                            onDecrement={forexDecrement}
                            onClear={forexClear}
                          />
                        ) : (
                          <>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <button
                            onClick={decrementQuantity}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg font-medium transition-colors"
                          >
                            <FontAwesomeIcon icon={faMinus} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={incrementQuantity}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg font-medium transition-colors"
                          >
                            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                          </button>
                        </div>

                       <div className="grid grid-cols-2 gap-2 pb-1">
                          <button
                            onClick={() => setQuantity(prev => Number(prev) + 10)}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            10
                          </button>
                          <button
                            onClick={() => setQuantity(prev => Number(prev) + 25)}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            25
                          </button>
                          <button
                            onClick={() => setQuantity(prev => Number(prev) + 100)}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            100
                          </button>
                          <button
                            onClick={() => setQuantity(prev => Number(prev) + 500)}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            500
                          </button>
                          <button
                            onClick={() => setQuantity(prev => Number(prev) + 1000)}
                            className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            1000
                          </button>
                          <button
                            onClick={() => setQuantity(0)}
                            className="bg-[#2a2a3e] hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            C
                          </button>
                        </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-800 mb-2 font-medium text-sm">Price</p>
                    <div className="flex items-center px-2 py-2 bg-gray-200 rounded-lg">
                      <input
                        type="text"
                        value={determineValue()}
                        onChange={(e) => {
                          const val = e.target.value;
                          const regex = /^\d*\.?\d{0,6}$/;
                          if (regex.test(val)) {
                            activeOrderTab === "limit" ? setLimitPrice(val) : setLossPrice(val);
                          }
                        }}
                        disabled={activeOrderTab === "market"}
                        className="w-full text-center bg-transparent border-none outline-none text-gray-800"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-800 mb-2 font-medium text-sm">Total</p>
                    <div className="flex items-center px-2 py-2 bg-gray-200 rounded-lg">
                      <input
                        type="text"
                        value={
                          Number(quantity) > 0
                            ? formatTotal(determineValue() * quantity)
                            : "0.00"
                        }
                        readOnly
                        className="w-full text-center bg-transparent border-none outline-none text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                <ProcessingButton
                  className="w-full h-12 rounded-full bg-[#004c50] text-white font-semibold shadow-lg cursor-pointer hover:bg-[#003a3d] transition-colors"
                  onClick={handlePlaceOrder}
                  processing={buySellLoading}
                  unavailable={!(Number(quantity) > 0)}
                  unavailableClassName="opacity-60"
                  processingLabel={isEditMode ? "Updating" : "Placing"}
                  label={isEditMode ? "Update Order" : "Place Order"}
                />

                <div className="pt-2 text-center text-black/60">
                  <p className="text-[12px] leading-tight whitespace-nowrap">
                    This is live simulated trading with virtual cash.
                  </p>
                  <p className="text-[11px] leading-tight whitespace-nowrap">
                    No brokerage order is sent, and nothing here is financial advice.
                  </p>
                  <p className="text-[11px] leading-tight whitespace-nowrap">
                    Learn in real time without risking real money.
                  </p>
                </div>
              </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileChartToolbar;